import { Image, Tag } from 'antd';
import qs from 'qs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  deleteComplainAction,
  fetchAllComplains,
  updateComplainAction
} from '../../../actions/complainsAction';
import CButton from '../../../component/Button';
import Copy from '../../../component/Copy';
import InnerHeader from '../../../component/InnerHeader';
import NoxTable from '../../../component/Table';
import formatDate from '../../../helpers/formatDate';

const Complains = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const urlLocation = useLocation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const currentProject = useSelector(({ project }) => project.currentProject);

  const onComplainDelete = () => {
    selectedRowKeys.forEach((index) => {
      const item = data[index];

      dispatch(
        deleteComplainAction(currentProject._id, item?._id, () => {
          dispatch(fetchAllComplains());
        })
      );
    });

    setSelectedRowKeys([]);
  };

  const onComplainCloseCase = () => {
    selectedRowKeys.forEach((index) => {
      const item = data[index];

      const selectedCustomer = { ...complains[index] };

      if (selectedCustomer.status) {
        selectedCustomer.status = 'resolved';
      } else {
        selectedCustomer.status = 'resolved';
      }

      dispatch(
        updateComplainAction(currentProject._id, item._id, selectedCustomer, () => {
          dispatch(fetchAllComplains());
        })
      );
    });

    setSelectedRowKeys([]);
  };

  const mapToTable = (complains) => {
    const data = [];

    if (!complains || complains.length === 0) {
      return data;
    }

    complains.forEach(({ _id, item, reporter, reason, createdAt, status, type, user }, index) => {
      data.push({
        _id,
        createdAt: formatDate(createdAt),
        key: index,
        link: type === 'user' ? user?.profilePictureUrl : item?.imageUrl,
        reason,
        sellerId: reporter?.userId,
        status: status === 'inReview' ? 'open' : status,
        type,
        userName: reporter ? `@${reporter?.userName}` : ''
      });
    });

    return data;
  };

  const filterListType = ['All', 'Item', 'User'];
  const filterListStatus = ['All', 'open', 'resolved', 'untouched'];

  const getInitialFilterFromUrl = () => {
    const { filter } = qs.parse(urlLocation.search, {
      ignoreQueryPrefix: true
    });

    if (typeof filter === 'string') {
      return [filter, 'All'];
    }

    return filter || [filterListStatus[0], filterListType[0]];
  };

  const openDetail = (item) => {
    history.push({
      pathname: `/authentication/complains/${item._id}`
    });
  };

  useEffect(() => {
    async function load() {
      dispatch(fetchAllComplains());
    }

    if (currentProject) {
      load();
    }
  }, [dispatch, currentProject]);

  const onFilterChange = (value, index, selectedFilter) => {
    const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

    if (index === 0) {
      params.filter = [value, selectedFilter];
    } else {
      params.filter = [selectedFilter, value];
    }

    history.push({
      pathname: `/authentication/complains`,
      search: qs.stringify(params)
    });
  };

  const filterData = (data) => {
    const selectedFilter = getInitialFilterFromUrl();

    if (selectedFilter[0] === 'All' && selectedFilter[1] === 'All') {
      return data;
    }
    if (selectedFilter[0] === 'All' && selectedFilter[1] !== 'All') {
      return data.filter((item) => item.status === selectedFilter[1]);
    }
    if (selectedFilter[0] !== 'All' && selectedFilter[1] === 'All') {
      return data.filter((item) => item.type === selectedFilter[0]?.toLowerCase());
    }
    return data.filter(
      (item) => item.type === selectedFilter[0]?.toLowerCase() && item.status === selectedFilter[1]
    );
  };

  const isLoaded = useSelector(({ complains }) => complains.isLoaded);
  const complains = useSelector(({ complains }) => complains.complains);
  const data = mapToTable(complains);
  const filteredData = filterData(data);

  const columns = [
    {
      dataIndex: 'link',
      key: 'link',
      render: (link) => (
        <Image height={48} src={link} style={{ objectFit: 'contain' }} width={48} />
      ),
      title: () => {
        return <div>Preview</div>;
      }
    },
    {
      dataIndex: '_id',
      key: '_id',
      render: (_id) => {
        return <Copy text={_id} />;
      },
      sorter: (a, b) => a._id.localeCompare(b._id),
      title: 'Report ID'
    },
    {
      dataIndex: 'userName',
      key: 'userName',
      render: (userName) => {
        return <Copy text={userName} />;
      },
      sorter: (a, b) => a.userName.localeCompare(b.userName),
      title: 'Seller'
    },
    {
      dataIndex: 'sellerId',
      key: 'sellerId',
      render: (sellerId) => {
        return <Copy text={sellerId} />;
      },
      sorter: (a, b) => a.sellerId.localeCompare(b.sellerId),
      title: 'Seller ID'
    },
    {
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => {
        return <Copy text={reason} />;
      },
      sorter: (a, b) => a.reason.localeCompare(b.reason),
      title: 'Reason'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let style = {};
        switch (status) {
          case 'open':
            style = {
              backgroundColor: 'rgba(65, 204, 156, 0.15)',
              border: 'none',
              color: '#41CC9C',
              width: '80%'
            };
            break;
          case 'resolved':
            style = {
              backgroundColor: 'rgba(155, 160, 173, 0.15)',
              border: 'none',
              color: '#9BA0AD',
              width: '80%'
            };
            break;
          case 'untouched':
            style = {
              backgroundColor: 'rgba(85, 100, 248, 0.15)',
              border: 'none',
              color: '#5564F8',
              width: '80%'
            };
            break;
          case 'blocked':
            style = {
              backgroundColor: 'rgba(255, 71, 108, 0.15)',
              border: 'none',
              color: '#FF476C',
              width: '80%'
            };
            break;
          default:
            break;
        }
        return (
          <Tag key={status} style={style}>
            {status}
          </Tag>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => {
        return <Copy text={createdAt} />;
      },
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      title: 'Date & Time'
    },
    {
      key: 'CloseCase',
      render: (item) => {
        return (
          <CButton
            disabled={item.status === 'resolved'}
            onClick={() => openDetail(item)}
            type={item.status === 'resolved' ? 'disabled' : 'edit'}
          >
            Close Case
          </CButton>
        );
      }
    }
  ];

  const actions = [
    {
      title: 'Download .CSV',
      type: 'primary'
      // onClick: {},
    }
  ];

  if (selectedRowKeys.length && complains[selectedRowKeys[0]]?.status !== 'resolved') {
    actions.unshift({
      onClick: onComplainCloseCase,
      title: 'Close Case'
    });
  }

  if (selectedRowKeys.length) {
    actions.unshift({
      onClick: onComplainDelete,
      title: 'Delete'
    });
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={complains.length} title="Complains" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={filteredData}
        filterList={[filterListType, filterListStatus]}
        loading={!isLoaded}
        onFilterChange={onFilterChange}
        searchBy="userName"
        selectedFilter={getInitialFilterFromUrl()}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
    </div>
  );
};

export default Complains;
